<script setup lang="ts">
import { computed } from 'vue';
import { usePreconUserProfilesOptions } from '../composables/usePreconUserProfilesOptions';
import type { UserNameInput } from '../helpers/normalizeUserName';
import { userAvatarLetters } from '../helpers/userAvatarLetters';

const props = withDefaults(
    defineProps<{
        /**
         * Shows the avatar in the "loading"-state.
         *
         * @default false
         */
        loading?: boolean;
        /**
         * A name object for the user that will be displayed.
         */
        userName?: UserNameInput;
        /**
         * Overwrite the fallback letters for when the name could not be found.
         */
        fallback?: string | null;
    }>(),
    {
        loading: false,
        fallback: null,
        userName: null,
    }
);

const options = usePreconUserProfilesOptions();

const fallbackLetters = computed(() =>
    userAvatarLetters(
        props.userName,
        props.fallback ?? options.value.defaultAvatarFallbackLetters
    )
);
</script>

<template>
    <span class="user-avatar no-picture">
        <template v-if="loading">
            <i class="pi pi-spinner pi-spin" />
        </template>
        <template v-else>
            {{ fallbackLetters }}
        </template>
    </span>
</template>

<style scoped lang="scss">
.user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 10px;

    &.lg {
        width: 52px;
        height: 52px;
        border-radius: 16px;
        font-size: 1.4rem;
    }

    &.xl {
        width: 5rem;
        height: 5rem;
        border-radius: 20px;
        font-size: 2rem;
    }

    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: var(--primary-lighter-color);
}
</style>
